import React, {FC, useContext} from 'react';
import {ModerationUser} from "interfaces";
import {
    AppEmojis,
    AppNames,
    areBoostsAvailable,
    areGreetingsAvailable,
    doesNeedAppInfo,
    GenderBackground,
    GenderName,
    SexualityLabel,
    toStatusString
} from "enums";
import {ModerationRow} from "components";
import {DateTime} from "luxon";
import {ModerationReports} from "./ModerationReports";
import {ModerationTags} from "./ModerationTags";
import {AuthContext} from "contexts";
import {ModerationAbout} from "components/ModerationUserCard/components/ModerationAbout";
import {ModerationStats} from "components/ModerationUserCard/components/ModerationStats";
import {ModerationDev} from "components/ModerationUserCard/components/ModerationDev";
import {ModerationGeo} from "components/ModerationUserCard/components/ModerationGeo";
import {ModerationPremium} from "components/ModerationUserCard/components/ModerationPremium";
import {ModerationStatus} from "components/ModerationUserCard/components/ModerationStatus";
import {ModerationBuckets} from "components/ModerationUserCard/components/ModerationBuckets";
import {ModerationSection} from "components/ModerationSection";
import {ModerationDownloadData} from "components/ModerationUserCard/components/ModerationDownloadData";
import {InterestGroupsName} from "enums/InterestGroups";
import {ModerationIPAddresses} from "components/ModerationUserCard/components/ModerationIPAddresses";
import {toStatusChangesReasonString} from "enums/UserStatusChangeReason";
import {ModerationDuplicates} from "components/ModerationUserCard/components/ModerationDuplicates";
import {ModerationName} from "components/ModerationUserCard/components/ModerationName";
import {SupportActions} from "enums/SupportActions";

interface Props {
    user: ModerationUser
    onUserChange: (user: ModerationUser) => void;
}

export const ModerationInfo: FC<Props> = ({user, onUserChange}) => {
    const {env, user: myUser, hasRight} = useContext(AuthContext);
    const reviewStatus = user.statusHistory ? user.statusHistory[user.statusHistory?.length - 1] : undefined;

    return <>
        <div>
            <div className="d-flex flex-row align-items-center">
                <ModerationName user={user}/>
                <ModerationDownloadData user={user}/>
            </div>
            <small>{toStatusString(user.status)}</small><br/>
            <small
                className={user.wasReviewed ? '' : 'fw-bold'}>{reviewStatus ? toStatusChangesReasonString(reviewStatus.reason, false) : ''}</small><br/>
            {user.isVerified && <><small>✅ Верифицированный</small><br/></>}
            {user.hasPremium && <><small>⭐ Премиум</small><br/></>}
            <div className="mb-3"/>

        </div>
        <div className="bg-light p-2 small text-black rounded p-2">
            <ModerationSection label="Статус" location={'top'}>
                <ModerationRow label={user.wasReviewed ? "Изменение статуса" : "Попадание в ревью"}
                               value={user.wasReviewed ? user.reviewedDate : reviewStatus?.date} isInsideTable/>
                <ModerationRow label="Регистрация" value={user.registrationDate} isInsideTable/>
                <ModerationRow label="Онлайн" value={user.onlineDate} isInsideTable/>
                <ModerationRow label="Требуется ревью" value={!user.wasReviewed} isInsideTable/>
            </ModerationSection>
            <ModerationSection label="Общее">
                {env && doesNeedAppInfo(env.app) &&
                    <ModerationRow label="Приложение" value={`${AppEmojis[user.app]} ${AppNames[user.app]}`}
                                   isInsideTable/>}
                <ModerationRow label="Пол"
                               value={<span style={GenderBackground[user.gender]}>{GenderName[user.gender]}</span>}
                               isInsideTable/>
                {user.isCouple && <ModerationRow label="Пара" value={user.isCouple} isInsideTable/>}
                <ModerationRow label="Ориентация" value={SexualityLabel[user.sexuality]} isInsideTable/>
                {user.metaTags.map(ig =>
                    <ModerationRow
                        key={ig.groupId}
                        label={InterestGroupsName[ig.groupId] ?? ig.groupId}
                        value={ig.interests.map(i => i.tag)?.join(', ')} isInsideTable/>
                )}
                {user.isPaused && <ModerationRow label="На паузе" value={user.isPaused} isInsideTable/>}
            </ModerationSection>
            <ModerationAbout user={user} onUserChange={onUserChange}/>
            <ModerationRow label="Ice Breaker" value={user.customIcebreakers?.map(i => i.text).join(', ')}/>
            <ModerationRow label="Любит части тел" value={user.attractedToFeatures?.map(f => f.text).join(', ')}/>
            <ModerationGeo user={user}/>
            <ModerationIPAddresses user={user}/>
            <ModerationDuplicates user={user}/>
            <ModerationPremium user={user}/>
            <ModerationTags user={user}/>
            <ModerationStats user={user}/>
            <ModerationReports
                label="Входящие Жалобы"
                reports={user.reports}
                reportsCount={user.reportsCount}
                userName={user.name}
                userKey={user.id}
                userGender={user.gender}
                warning={{minCount: 1, label: 'На юзера уже есть жалобы'}}
            />
            <ModerationReports
                label="Исходящие Жалобы" reports={user.outgoingReports}
                reportsCount={user.outgoingReportsCount}
                warning={{minCount: 10, label: 'Юзер выставляет много жалоб'}}
                userKey={user.id}
                userName={user.name}
                userGender={user.gender}
            />
            {user.scamMessages.length > 0 &&
                <ModerationRow
                    label="Scam сообщения"
                    value={user.scamMessages?.map((m, i) =>
                        <p key={`${user.id}_${i}`} className="mb-2">{m}</p>)}/>}
            <ModerationStatus user={user}/>
            {hasRight(SupportActions.SetGiftGreeting) &&
                <>
                    {env && areBoostsAvailable(env.app) &&
                        <ModerationBuckets label="Boosts"
                                           buckets={user.boostBuckets}
                                           type={"boosts"}
                        />}
                    {env && areGreetingsAvailable(env.app) &&
                        <ModerationBuckets label="Sparks"
                                           buckets={user.greetingBuckets}
                                           type={'sparks'}
                        />}
                </>
            }
            <ModerationDev user={user} onUserChange={onUserChange}/>
            {user.temporaryUnbannedDate &&
                <ModerationRow
                    label="Дата временной разблокировки"
                    value={DateTime.fromJSDate(user.temporaryUnbannedDate).toLocaleString(DateTime.DATETIME_SHORT)}/>
            }
            <ModerationRow label="ID" value={user.id}/>
        </div>
    </>;
}

